import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { InputAdornment, IconButton, TextField, Button, Alert } from "@mui/material";

import { getApiCall, postApiCall } from "../../utils/Apicalls";
import SpinnerShow from "../../components/Spinner";
import "./signup.css";

function SignUpScreen() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [showPassword, setShowPassword] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [msgType, setMsgType] = useState("");
  const [error, setError] = useState("");

  // Check if user is already logged in, redirect to interview page if they are
  useEffect(() => {
    const userDetails = localStorage.getItem("userDetails");
    if (userDetails) {
      // User is already logged in, redirect to interview page
      navigate("/home");
    }
  }, [navigate]);

  // Use useCallback to memoize the function so it doesn't change on every render
  const checkValidUser = useCallback(async () => {
    setLoading(true);
    setMsgType("invalid");
    setError("");
    
    try {
      const result = await getApiCall(`interview-app/auth/check/${id}`);
      setLoading(false);
      
      if (result?.status) {
        setMsgType("valid");
      } else {
        setError("The signup link is invalid or has expired.");
      }
    } catch (err) {
      setLoading(false);
      setError("An error occurred while validating your signup link. Please try again later.");
      console.error("Validation error:", err);
    }
  }, [id]);

  useEffect(() => {
    // Only check valid user if not already logged in
    const userDetails = localStorage.getItem("userDetails");
    if (!userDetails) {
      checkValidUser();
    }
  }, [checkValidUser]);

  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  const validateForm = () => {
    if (!firstName.trim()) {
      setError("Please enter your name");
      return false;
    }
    
    if (!userName.trim()) {
      setError("Please enter your email");
      return false;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userName)) {
      setError("Please enter a valid email address");
      return false;
    }
    
    if (!password) {
      setError("Please enter a password");
      return false;
    }
    
    if (password.length < 6) {
      setError("Password must be at least 6 characters long");
      return false;
    }
    
    return true;
  };

  const onSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError("");
    
    try {
      const result = await postApiCall(`interview-app/auth/setup`, {
        id: id,
        username: userName,
        firstName: firstName,
        password: password,
      });
      
      setLoading(false);
      
      if (result?.status) {
        setMsgType("redirectLogin");
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      } else {
        setError(result?.message || "Failed to create account. Please try again.");
      }
    } catch (err) {
      setLoading(false);
      setError("An error occurred while creating your account. Please try again later.");
      console.error("Setup error:", err);
    }
  };

  const handleLoginClick = (e) => {
    e.preventDefault();
    navigate("/login");
  };

  return (
    <>
      <div className="login-container">
        <div className="screen">
          <div className="screen__content">
            {loading && (
              <div style={{ marginTop: 20 }}>
                <SpinnerShow />
              </div>
            )}
            
            {error && !loading && (
              <div style={{ padding: "20px 30px 0" }}>
                <Alert severity="error">{error}</Alert>
              </div>
            )}
            
            {!loading && msgType === "valid" && (
              <div className="login">
                <h2 className="signup-title">Create Your Account</h2>
                
                <div className="login__field">
                  <TextField
                    id="outlined-basic"
                    label="Name"
                    variant="outlined"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Enter your full name"
                    className="w-100"
                    fullWidth
                  />
                </div>
                
                <div className="login__field">
                  <TextField
                    id="outlined-basic"
                    label="Email"
                    variant="outlined"
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    placeholder="Enter your email"
                    className="w-100"
                    fullWidth
                    type="email"
                  />
                </div>
                
                <div className="login__field">
                  <TextField
                    id="outlined-basic"
                    label="Password"
                    variant="outlined"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-100"
                    fullWidth
                    type={showPassword ? "text" : "password"}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleTogglePassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
                
                <button className="button login__submit" onClick={onSubmit}>
                  <span className="button__text">Create Account</span>
                </button>
                
                <div className="login-link">
                  Already have an account? <button 
                    className="text-button" 
                    onClick={handleLoginClick}
                  >
                    Log in
                  </button>
                </div>
              </div>
            )}
            
            {!loading && msgType === "invalid" && (
              <div className="login">
                <h1>Invalid Signup Link</h1>
                <p>The signup link you're using is invalid or has expired.</p>
                <Button 
                  variant="contained" 
                  onClick={() => navigate("/login")}
                  style={{ marginTop: 20 }}
                >
                  Go to Login
                </Button>
              </div>
            )}
            
            {!loading && msgType === "redirectLogin" && (
              <div className="login">
                <h3>Account Created Successfully!</h3>
                <p>Redirecting you to the login page in a few seconds...</p>
                <Button 
                  variant="contained" 
                  onClick={() => navigate("/login")}
                  style={{ marginTop: 20 }}
                >
                  Go to Login
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default SignUpScreen;
